<template>
    <div class="portlet light">
        <div class="portlet-title">
            <div class="caption">
                <span class="caption-subject font-green-sharp bold uppercase"
                    id="TituloBalanceteGerencial">Fluxo de Caixa Gerencial</span>
            </div>
        </div>
        <div class="portlet-body">
            <fluxo-caixa-gerencial-filtro color-selected="blue" color-deselected="white"></fluxo-caixa-gerencial-filtro>
            <wes-message v-bind="messagePanel"></wes-message>

            <!-- Checkboxes "Mostrar: Conta 1 2 / Centro de custo 1 2 / Projeto 1 2"
                 igual ao dxPivotGrid antigo. Cada dimensão tem um checkbox principal
                 (liga/desliga inteira) e checkboxes de profundidade por nível da
                 hierarquia. Auto-detectado baseado nos dados. -->
            <div v-if="exibirTabela" class="fcg-checkboxes-dimensoes">
                Mostrar:&nbsp;&nbsp;
                <template v-for="(dim, idx) in checkboxesDimensoes">
                    <label class="mt-checkbox" v-if="dim.tipo === 'principal'" :key="'dim-p-' + idx">
                        {{ dim.label }}&nbsp;&nbsp;
                        <input type="checkbox" v-model="dim.ref.ativo">
                        <span></span>
                    </label>
                    <label class="mt-checkbox" v-else :key="'dim-n-' + idx">
                        {{ dim.label }}&nbsp;&nbsp;
                        <input type="checkbox" v-model="dim.ref.niveisVisiveis[dim.indiceNivel]">
                        <span></span>
                    </label>
                </template>
            </div>

            <!-- Tabela única em Vue puro: 3 seções (Saldo Inicial / Movimentações
                 hierárquicas com expand/collapse / Saldo Final), scroll único,
                 larguras alinhadas, sem hacks. -->
            <div v-if="exibirTabela" class="fcg-exportar-wrapper">
                <button type="button" class="btn btn-sm btn-default" @click="colapsarNivel" title="Colapsar um nível">
                    <i class="fa fa-minus"></i>
                </button>
                <button type="button" class="btn btn-sm btn-default" @click="expandirNivel" title="Expandir um nível">
                    <i class="fa fa-plus"></i>
                </button>
                <button type="button"
                        class="btn btn-sm btn-default fcg-btn-toggle"
                        :class="{ active: agrupaPorTipoConta }"
                        @click="$store.commit('pivotgrid/toggleAgrupaPorTipoConta')"
                        title="Agrupa as contas bancárias por tipo de conta ao mostrar saldos">
                    <i class="fa fa-list"></i> Agrupar tipo de conta
                </button>
                <button type="button" class="btn btn-sm btn-default" @click="exportarExcel">
                    <i class="fa fa-file-excel-o"></i> Exportar Excel
                </button>
            </div>

            <fluxo-caixa-tabela
                v-if="exibirTabela"
                ref="tabelaFluxo"
                :movimentacoes="data || []"
                :saldo-inicial-data="saldoInicialData"
                :saldo-final-data="saldoFinalData"
                :previsto-realizado="previstoRealizadoAtual"
                :config-dimensoes="dimensoes"
                :agrupa-por-tipo-conta="agrupaPorTipoConta"
                @cell-click="onCelulaClique">
            </fluxo-caixa-tabela>
            <br />
        </div>
    </div>
</template>

<style>
.fcg-checkboxes-dimensoes {
    margin: 8px 0;
    font-size: 13px;
}
.fcg-checkboxes-dimensoes .mt-checkbox {
    margin-right: 16px;
    display: inline-block;
}
.fcg-exportar-wrapper {
    margin: 8px 0;
    text-align: right;
}
.fcg-btn-toggle.active {
    background-color: #5b9bd5;
    border-color: #4a86bf;
    color: #fff;
}
.fcg-btn-toggle.active:hover {
    background-color: #4a86bf;
}
</style>

<script>
// PivotGrid.vue — apesar do nome legado, este componente NÃO usa mais o
// dxPivotGrid. Ele é um wrapper que:
//   1. Subscreve no Vuex (begin/end request).
//   2. Pre-processa os dados (separa 2.x de saldos sintéticos, calcula matrizes
//      de Saldo Inicial/Final por conta-tesouraria).
//   3. Renderiza FluxoCaixaTabela.vue (tabela única em Vue puro).
//
// O nome do arquivo foi mantido pra evitar quebrar imports em outros lugares
// (controllers, registros de rota, etc.). Internamente é uma tabela HTML/Vue.
const localization = require('devextreme/localization');
localization.locale("pt-BR");

import FluxoCaixaTabela from "./FluxoCaixaTabela.vue";

// Normaliza qualquer representação de data para "YYYY-MM-DD" ou null.
// Aceita: ISO "YYYY-MM-DD", ISO com horário, .NET "/Date(ms)/", "DD/MM/YYYY", Date, moment.
function normalizarParaISO(d) {
    if (!d) return null;
    if (typeof d === 'object' && typeof d.toDate === 'function') d = d.toDate();
    if (d instanceof Date) {
        if (isNaN(d.getTime())) return null;
        return d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2);
    }
    if (typeof d === 'string') {
        var isoMatch = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) return isoMatch[1] + '-' + isoMatch[2] + '-' + isoMatch[3];
        var brMatch = d.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (brMatch) return brMatch[3] + '-' + brMatch[2] + '-' + brMatch[1];
        var netMatch = d.match(/\/Date\((\d+)\)\//);
        if (netMatch) {
            var nd = new Date(+netMatch[1]);
            if (!isNaN(nd.getTime()))
                return nd.getFullYear() + '-' + ('0' + (nd.getMonth() + 1)).slice(-2) + '-' + ('0' + nd.getDate()).slice(-2);
        }
    }
    if (typeof d === 'number') {
        var nd2 = new Date(d);
        if (!isNaN(nd2.getTime()))
            return nd2.getFullYear() + '-' + ('0' + (nd2.getMonth() + 1)).slice(-2) + '-' + ('0' + nd2.getDate()).slice(-2);
    }
    return null;
}

export default {
    components: { FluxoCaixaTabela },
    data: function () {
        return {
            messagePanel: {
                type: "",
                message: "",
                title: ""
            },
            data: null,
            // Matrizes pré-computadas pra Saldo Inicial e Saldo Final
            // (componente filho FluxoCaixaTabela só renderiza)
            saldoInicialData: null,
            saldoFinalData: null,
            previstoRealizadoAtual: false,
            // Configuração das dimensões hierárquicas (Conta, Centro de custo, Projeto)
            // — paridade com checkboxes "Mostrar: Conta 1 2 / Centro 1 2 / Projeto 1 2"
            // que existiam no dxPivotGrid antigo. Auto-detectado em loadData baseado
            // nos dados (cada dimensão pode ter N níveis dependendo da máscara do plano).
            dimensoes: {
                conta:        { ativo: true, niveisVisiveis: [], temDados: false },
                centroCusto:  { ativo: true, niveisVisiveis: [], temDados: false },
                projeto:      { ativo: true, niveisVisiveis: [], temDados: false }
            }
        };
    },
    computed: {
        exibirTabela: function () {
            return (this.data && this.data.length > 0)
                || (this.saldoInicialData && this.saldoInicialData.contas && this.saldoInicialData.contas.length > 0);
        },
        // Lista plana de checkboxes pra renderizar no template. Cada item:
        //   - Principal: { tipo: 'principal', label: 'Conta', ref: this.dimensoes.conta }
        //   - Nível: { tipo: 'nivel', label: '1', ref: this.dimensoes.conta, indiceNivel: 0 }
        // Só inclui dimensões que têm dados (temDados: true).
        agrupaPorTipoConta: function () {
            return this.$store.getters['pivotgrid/isAgrupaPorTipoConta'];
        },
        checkboxesDimensoes: function () {
            var lista = [];
            var labelsDim = { conta: 'Conta', centroCusto: 'Centro de custo', projeto: 'Projeto' };
            var self = this;
            ['conta', 'centroCusto', 'projeto'].forEach(function (key) {
                var dim = self.dimensoes[key];
                if (!dim || !dim.temDados) return;
                lista.push({ tipo: 'principal', label: labelsDim[key], ref: dim });
                // Checkboxes de profundidade — um por nível
                for (var i = 0; i < (dim.niveisVisiveis || []).length; i++) {
                    lista.push({ tipo: 'nivel', label: String(i + 1), ref: dim, indiceNivel: i });
                }
            });
            return lista;
        }
    },
    mounted: function () {
        let vm = this;
        this.$store.subscribe((mutation, state) => {
            if (mutation.type == 'pivotgrid/beginRequest') {
                vm.beginRequest();
            }
            else if (mutation.type == 'pivotgrid/endRequestSuccess') {
                vm.loadData(state.pivotgrid.dataset, state.pivotgrid.dataSetSaldosBancarios);
            }
            else if (mutation.type == 'pivotgrid/endRequestError') {
                vm.endRequestError(state.pivotgrid.error);
            }
        });
    },
    methods: {
        onCelulaClique: function (dados) {
            var contas = this.$store.state.treeview.treeview_selecoes[2];
            ShowAnalitico({
                plano:             contas.origem,
                data:              dados.data,
                tipo:              dados.tipo,
                ehCC:              dados.ehCC,
                ehProjeto:         dados.ehProjeto,
                estrutura:         dados.estrutura,
                estruturaAuxiliar: dados.estruturaAuxiliar,
                estruturaProjeto:  dados.estruturaProjeto,
                gerencial:         contas.tipo === 'Gerenciais',
                filtro:            this.$store.state.filtro,
                treeview:          this.$store.state.treeview.treeview_selecoes.filter(x=>x.id != "projetos" && x.data != 0)
            });
        },
        exportarExcel: function () {
            if (this.$refs.tabelaFluxo && this.$refs.tabelaFluxo.exportarExcel) {
                this.$refs.tabelaFluxo.exportarExcel();
            }
        },
        expandirNivel: function () {
            if (this.$refs.tabelaFluxo && this.$refs.tabelaFluxo.expandirNivel) {
                this.$refs.tabelaFluxo.expandirNivel();
            }
        },
        colapsarNivel: function () {
            if (this.$refs.tabelaFluxo && this.$refs.tabelaFluxo.colapsarNivel) {
                this.$refs.tabelaFluxo.colapsarNivel();
            }
        },
        blockUi: function () {
            window.App.blockUI({
                target: this.$el,
                message: '<h3>Buscando dados...</h3>',
            });
        },
        beginRequest: function () {
            this.messagePanel.message = "";
            this.blockUi();
        },
        endRequestError: function (error) {
            window.App.unblockUI(this.$el);
            this.messagePanel.type = "danger";
            this.messagePanel.message = error;
        },
        loadData: function (data, saldosBancarios) {
            window.App.unblockUI(this.$el);
            this.messagePanel.message = "";

            var previstoRealizado = this.$store.state.filtro.previstoRealizado;
            this.previstoRealizadoAtual = previstoRealizado;

            var contas = (saldosBancarios && saldosBancarios.contas) || [];
            var movimentosRealizadoSql = (saldosBancarios && saldosBancarios.movimentosRealizado) || [];

            // Filtra só linhas 2.x do plano gerencial (descarta 1.x — Disponibilidades —
            // que viram saldos sintéticos calculados separadamente).
            var raizConta = function (n) {
                return n ? String(n).split('|')[0].trim() : null;
            };
            var isDisp = function (vo) {
                var r = raizConta(vo.niveisConta);
                return r !== null && r.charAt(0) === '1';
            };
            var isResultado = function (vo) {
                var r = raizConta(vo.niveisConta);
                return r !== null && r.charAt(0) === '2';
            };

            var pInicio = normalizarParaISO(this.$store.state.options.startDate);
            var pFim    = normalizarParaISO(this.$store.state.options.endDate);

            var dataMovimentacoes = null;
            if (data != null) {
                dataMovimentacoes = data.filter(function (vo) { return !isDisp(vo); });

                // Remove registros com data fora do período solicitado
                if (pInicio || pFim) {
                    dataMovimentacoes = dataMovimentacoes.filter(function (vo) {
                        var dk = normalizarParaISO(vo.data);
                        if (!dk) return false;
                        if (pInicio && dk < pInicio) return false;
                        if (pFim && dk > pFim) return false;
                        return true;
                    });
                }

                if (contas.length > 0) {
                    var movimentosResultado = dataMovimentacoes.filter(isResultado);
                    var matriz = this.computarMatrizSaldos(contas, movimentosResultado, movimentosRealizadoSql);
                    if (matriz.contas.length > 0) {
                        this.saldoInicialData = {
                            contas: matriz.contas,
                            datas: matriz.datas,
                            valores: matriz.valoresInicial
                        };
                        this.saldoFinalData = {
                            contas: matriz.contas,
                            datas: matriz.datas,
                            valores: matriz.valoresFinal
                        };
                    } else {
                        this.saldoInicialData = null;
                        this.saldoFinalData = null;
                    }
                } else {
                    this.saldoInicialData = null;
                    this.saldoFinalData = null;
                }
            } else {
                this.saldoInicialData = null;
                this.saldoFinalData = null;
            }

            this.data = dataMovimentacoes;

            // Detecta quantos níveis cada dimensão tem (baseado nos dados) e
            // inicializa os checkboxes de profundidade. temDados controla se o
            // checkbox principal aparece — se nenhum registro tem niveisCentroCusto
            // preenchido, a dimensão "Centro de custo" some da UI.
            this.atualizarDimensoesPorDados(dataMovimentacoes || []);
        },

        // Conta níveis máximos de cada dimensão e atualiza o estado `dimensoes`.
        // Preserva o que o usuário já marcou (se já tinha N níveis e agora tem
        // mais N+M, os novos vêm visíveis por padrão).
        atualizarDimensoesPorDados: function (data) {
            var maxNiveis = { conta: 0, centroCusto: 0, projeto: 0 };
            var temDados = { conta: false, centroCusto: false, projeto: false };
            var campoNiveis = {
                conta: 'niveisConta',
                centroCusto: 'niveisCentroCusto',
                projeto: 'niveisProjeto'
            };
            data.forEach(function (vo) {
                Object.keys(campoNiveis).forEach(function (key) {
                    var raw = vo[campoNiveis[key]];
                    if (raw && String(raw).trim() !== '') {
                        var n = String(raw).split('|').filter(function (x) { return x; }).length;
                        if (n > maxNiveis[key]) maxNiveis[key] = n;
                        temDados[key] = true;
                    }
                });
            });

            var self = this;
            Object.keys(maxNiveis).forEach(function (key) {
                var dim = self.dimensoes[key];
                dim.temDados = temDados[key];
                // Estende niveisVisiveis com `true` se cresceu; corta se diminuiu.
                while (dim.niveisVisiveis.length < maxNiveis[key]) dim.niveisVisiveis.push(true);
                if (dim.niveisVisiveis.length > maxNiveis[key]) {
                    dim.niveisVisiveis.splice(maxNiveis[key]);
                }
            });
        },

        // ==================== Saldos por conta/dia ====================

        // Calcula matrizes de Saldo Inicial e Saldo Final por conta-tesouraria por dia.
        // Estratégia híbrida (cada coluna usa a fonte que dá distribuição correta):
        //   - Realizado, com TS_LANCAMENTOS (movimentosRealizadoSql): saldo evolui dia
        //     a dia POR CONTA via SQL (TS_LANCAMENTOS tem CONTATESOURARIA + sinal).
        //   - Previsto e Realizado-fallback: saldo inicial constante por conta + "Sem
        //     Conta Definida" acumula movimentos 2.x do legacy.
        //
        // Retorna: { contas: [{handle,nome}], datas: [Date],
        //            valoresInicial / valoresFinal: { [handle]: { [dataKey]: { Previsto, Realizado } } } }
        computarMatrizSaldos: function (contas, movimentos2xLegacy, movimentosRealizadoSql) {
            if (!contas || contas.length === 0) {
                return { contas: [], datas: [], valoresInicial: {}, valoresFinal: {} };
            }

            var previstoRealizado = this.$store.state.filtro.previstoRealizado;
            var tipos = previstoRealizado ? ['Previsto', 'Realizado'] : ['Realizado'];
            var SEM_CONTA = 'Sem Conta Definida';

            var diasMap = {};
            (movimentos2xLegacy || []).forEach(function (m) {
                var dk = normalizarParaISO(m.data);
                if (dk) diasMap[dk] = dk;
            });
            (movimentosRealizadoSql || []).forEach(function (m) {
                var dk = normalizarParaISO(m.data);
                if (dk) diasMap[dk] = dk;
            });
            var dataKeysOrdenados = Object.keys(diasMap).sort();

            var periodoInicio = normalizarParaISO(this.$store.state.options.startDate);
            var periodoFim    = normalizarParaISO(this.$store.state.options.endDate);
            if (periodoInicio || periodoFim) {
                dataKeysOrdenados = dataKeysOrdenados.filter(function (dk) {
                    if (periodoInicio && dk < periodoInicio) return false;
                    if (periodoFim && dk > periodoFim) return false;
                    return true;
                });
            }

            if (dataKeysOrdenados.length === 0 && periodoInicio && periodoFim) {
                var cur = periodoInicio;
                while (cur <= periodoFim) {
                    dataKeysOrdenados.push(cur);
                    var partes = cur.split('-');
                    var prox = new Date(+partes[0], +partes[1] - 1, +partes[2] + 1);
                    cur = prox.getFullYear() + '-' + ('0' + (prox.getMonth() + 1)).slice(-2) + '-' + ('0' + prox.getDate()).slice(-2);
                }
            }

            if (dataKeysOrdenados.length === 0) {
                return { contas: [], datas: [], valoresInicial: {}, valoresFinal: {} };
            }

            var datasOrdenadas = dataKeysOrdenados.map(function (dk) {
                var partes = dk.split('-');
                return new Date(+partes[0], +partes[1] - 1, +partes[2]);
            }).filter(function (d) { return !isNaN(d.getTime()); });

            var movsLegacyPorDiaTipo = {};
            (movimentos2xLegacy || []).forEach(function (m) {
                var dk = normalizarParaISO(m.data);
                var key = dk + '||' + (m.tipo || 'Realizado');
                movsLegacyPorDiaTipo[key] = (movsLegacyPorDiaTipo[key] || 0) + m.valor;
            });

            var movsSqlPorContaDia = {};
            var temSqlRealizado = !!(movimentosRealizadoSql && movimentosRealizadoSql.length > 0);
            (movimentosRealizadoSql || []).forEach(function (m) {
                var handle = m.contaTesourariaHandle == null ? 'NULL' : String(m.contaTesourariaHandle);
                var dk = normalizarParaISO(m.data);
                var key = handle + '||' + dk;
                movsSqlPorContaDia[key] = (movsSqlPorContaDia[key] || 0) + m.valor;
            });
            var temSemContaSql = (movimentosRealizadoSql || []).some(function (m) {
                return m.contaTesourariaHandle == null;
            });

            var contasExibir = contas.slice().map(function (c) {
                return { handle: c.handle, nome: c.nome, saldoAtual: c.saldoAtual || 0,
                         idTipoConta: c.idTipoConta, tipoConta: c.tipoConta };
            });
            contasExibir.push({ handle: null, nome: SEM_CONTA, saldoAtual: 0 });

            var valoresInicial = {};
            var valoresFinal = {};
            contasExibir.forEach(function (c) {
                var keyConta = c.handle == null ? 'NULL' : String(c.handle);
                valoresInicial[keyConta] = {};
                valoresFinal[keyConta] = {};
                dataKeysOrdenados.forEach(function (dk) {
                    valoresInicial[keyConta][dk] = { Previsto: 0, Realizado: 0 };
                    valoresFinal[keyConta][dk] = { Previsto: 0, Realizado: 0 };
                });
            });

            tipos.forEach(function (tipo) {
                if (tipo === 'Realizado' && temSqlRealizado) {
                    contasExibir.forEach(function (c) {
                        var keyConta = c.handle == null ? 'NULL' : String(c.handle);
                        var saldo = c.saldoAtual || 0;
                        dataKeysOrdenados.forEach(function (dk) {
                            valoresInicial[keyConta][dk][tipo] = saldo;
                            saldo += (movsSqlPorContaDia[keyConta + '||' + dk] || 0);
                            valoresFinal[keyConta][dk][tipo] = saldo;
                        });
                    });
                    return;
                }

                var acumulado = 0;
                dataKeysOrdenados.forEach(function (dk) {
                    var saldoInicialSemConta = acumulado;
                    var movDia = movsLegacyPorDiaTipo[dk + '||' + tipo] || 0;
                    acumulado += movDia;
                    var saldoFinalSemConta = acumulado;

                    contasExibir.forEach(function (c) {
                        var keyConta = c.handle == null ? 'NULL' : String(c.handle);
                        if (c.handle == null) {
                            valoresInicial[keyConta][dk][tipo] = saldoInicialSemConta;
                            valoresFinal[keyConta][dk][tipo] = saldoFinalSemConta;
                        } else {
                            valoresInicial[keyConta][dk][tipo] = c.saldoAtual || 0;
                            valoresFinal[keyConta][dk][tipo] = c.saldoAtual || 0;
                        }
                    });
                });
            });

            // Remove "Sem Conta Definida" se não houver resíduo
            if (!temSemContaSql) {
                var temResiduo = false;
                dataKeysOrdenados.forEach(function (dk) {
                    tipos.forEach(function (tipo) {
                        if ((valoresInicial['NULL'][dk][tipo] || 0) !== 0
                            || (valoresFinal['NULL'][dk][tipo] || 0) !== 0) {
                            temResiduo = true;
                        }
                    });
                });
                if (!temResiduo) {
                    contasExibir = contasExibir.filter(function (c) { return c.handle != null; });
                    delete valoresInicial['NULL'];
                    delete valoresFinal['NULL'];
                }
            }

            var contasFmt = contasExibir.map(function (c) {
                return { handle: c.handle == null ? 'NULL' : String(c.handle), nome: c.nome,
                         idTipoConta: c.idTipoConta, tipoConta: c.tipoConta };
            });

            return {
                contas: contasFmt,
                datas: datasOrdenadas,
                valoresInicial: valoresInicial,
                valoresFinal: valoresFinal
            };
        }
    }
};
//# sourceURL=PivotGrid.vue
</script>
